import React, { useState } from 'react';
type DeviceVariant = 'browser' | 'laptop' | 'phone';
interface DeviceFrameProps {
  variant?: DeviceVariant;
  /** Image source. Omit when passing custom children (e.g. a native illustrative UI). */
  src?: string;
  alt?: string;
  /**
   * Decorative chrome label (not a live URL). Prefer non-navigable copy like
   * "Simplify to Amplify · Reader home" so visitors are not tempted to visit it.
   */
  chromeLabel?: string;
  /** @deprecated Use chromeLabel. Kept for call sites that still pass url=. */
  url?: string;
  /** Aspect ratio for the screen area, e.g. "16 / 10". Prevents layout shift. */
  aspect?: string;
  /** Below-the-fold images should lazy-load. Hero/above-the-fold can pass "eager". */
  loading?: 'eager' | 'lazy';
  className?: string;
  children?: React.ReactNode;
  /** object-position for cropping full-page screenshots to their strongest region. */
  objectPosition?: string;
  /** Scale the screenshot inside the frame to focus a region (homepage crops). */
  imageScale?: number;
}
function BrowserChrome({
  label
}: {
  label: string;
}) {
  return <div className="flex items-center gap-2 border-b border-gray-200/80 bg-paper-dark px-3 py-2 sm:px-4 sm:py-2.5" data-id="element-693" data-mp="ccv7xtuins5o">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-694" data-mp="3uj058vtmr7b1" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-695" data-mp="3cqaocj1ea58" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840] sm:h-3 sm:w-3" aria-hidden="true" data-id="element-696" data-mp="r0zuj8wowntq" />
      <div className="ml-2 flex min-w-0 flex-1 select-none items-center rounded-md bg-white px-2.5 py-1 text-[0.7rem] font-medium text-text-muted sm:ml-3 sm:px-3 sm:text-xs" aria-hidden="true" data-id="element-697" data-mp="w0m67dwvdw0f">
        <span className="truncate" data-id="element-698" data-mp="m9rj67bsusdx">{label}</span>
      </div>
    </div>;
}
function Screen({
  src,
  alt,
  aspect,
  loading = 'lazy',
  objectPosition,
  imageScale,
  children
}: Pick<DeviceFrameProps, 'src' | 'alt' | 'aspect' | 'loading' | 'objectPosition' | 'imageScale' | 'children'>) {
  const [errored, setErrored] = useState(false);
  const pendingFile = src ? src.split('/').pop() : undefined;
  return <div className="relative w-full overflow-hidden bg-white" style={src && aspect ? {
    aspectRatio: aspect
  } : undefined} data-id="element-699" data-mp="1jorejj2ag38x">
      {src && !errored ? <img src={src} alt={alt ?? ''} loading={loading} decoding="async" onError={() => setErrored(true)} className="h-full w-full object-cover object-top" style={{
      ...(objectPosition ? {
        objectPosition
      } : {}),
      ...(imageScale && imageScale !== 1 ? {
        transform: `scale(${imageScale})`,
        transformOrigin: objectPosition || 'center center'
      } : {})
    }} data-id="element-700" data-mp="1eqhil68qoym9" /> : src && errored ? <div className="flex h-full w-full flex-col items-center justify-center gap-2 border border-dashed border-gray-300 bg-paper-dark p-6 text-center" data-id="element-701" data-mp="qz2dx6thq57u">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted" data-id="element-702" data-mp="mwxko96yfalq">
            Proof asset pending
          </p>
          {pendingFile ? <code className="max-w-full truncate rounded bg-white px-2 py-1 text-[0.7rem] text-navy ring-1 ring-gray-200" data-id="element-703" data-mp="2tlzhwteiptyw">
              {pendingFile}
            </code> : null}
        </div> : children}
    </div>;
}
export function DeviceFrame({
  variant = 'browser',
  src,
  alt,
  chromeLabel,
  url,
  aspect = '16 / 10',
  loading = 'lazy',
  className = '',
  children,
  objectPosition,
  imageScale
}: DeviceFrameProps) {
  const label = chromeLabel ?? url ?? 'Reader path';
  if (variant === 'phone') {
    return <div className={`relative mx-auto w-full max-w-[300px] rounded-[2.5rem] border-[10px] border-navy bg-navy p-0 shadow-[0_40px_80px_-30px_rgba(26,40,56,0.55)] ${className}`} data-id="element-704" data-mp="2ew1s690xww7l">
        <div className="absolute left-1/2 top-2.5 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/25" data-id="element-705" data-mp="2omcwhwojd7f0" />
        <div className="overflow-hidden rounded-[1.9rem]" data-id="element-706" data-mp="1r7sb1keo8wms">
          <Screen src={src} alt={alt} aspect={aspect ?? '9 / 19'} loading={loading} objectPosition={objectPosition} imageScale={imageScale} data-id="element-707" data-mp="1r5633jnvcf41">
            {children}
          </Screen>
        </div>
      </div>;
  }
  if (variant === 'laptop') {
    return <div className={`mx-auto w-full overflow-x-clip ${className}`} data-id="element-708" data-mp="3tup80n1a7l4c">
        <div className="rounded-t-2xl border-[3px] border-b-0 border-navy bg-navy p-2 shadow-[0_40px_80px_-30px_rgba(26,40,56,0.5)]" data-id="element-709" data-mp="bfwrfe093mbw">
          <div className="overflow-hidden rounded-lg bg-white" data-id="element-710" data-mp="6o5a7u2zisyf">
            <BrowserChrome label={label} data-id="element-711" data-mp="le48dwuji4dw" />
            <Screen src={src} alt={alt} aspect={aspect} loading={loading} objectPosition={objectPosition} imageScale={imageScale} data-id="element-712" data-mp="lct1vp9pmij3">
              {children}
            </Screen>
          </div>
        </div>
        {/* Decorative base — clipped by parent so 112% width cannot cause page overflow */}
        <div className="relative mx-auto h-3 w-[112%] max-w-none -translate-x-[5.4%] rounded-b-2xl bg-navy-light" aria-hidden="true" data-id="element-713" data-mp="qeigfew55ebx">
          <div className="absolute left-1/2 top-0 h-1.5 w-24 -translate-x-1/2 rounded-b-lg bg-navy" data-id="element-714" data-mp="2w88elsvo5rnv" />
        </div>
      </div>;
  }

  // browser
  return <div className={`overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_40px_80px_-30px_rgba(26,40,56,0.55)] ring-1 ring-black/5 ${className}`} data-id="element-715" data-mp="2i0dge26s3i3g">
      <BrowserChrome label={label} data-id="element-716" data-mp="28atxiwj2fo8l" />
      <Screen src={src} alt={alt} aspect={aspect} loading={loading} objectPosition={objectPosition} imageScale={imageScale} data-id="element-717" data-mp="289r70ppnnnt7">
        {children}
      </Screen>
    </div>;
}